import Task from "../../interfaces/task/Task";

export interface TaskDetailCacheScope {
  actorId: string;
  effectiveUserId: string;
  organizationId: number;
}

interface CacheEntry { task: Task; expiresAt: number; lastAccessedAt: number; }

const TTL_MS = 30_000;
const MAX_ENTRIES = 20;
const REPRESENTATION_VERSION = "v1";
const entries = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<Task | null>>();
let generation = 0;

export class TaskDetailCacheObsoleteError extends Error {
  constructor() {
    super("Task-detail response belongs to an obsolete cache generation.");
    this.name = "TaskDetailCacheObsoleteError";
  }
}

const clone = <T>(value: T): T => {
  if (typeof structuredClone === "function") return structuredClone(value);
  return JSON.parse(JSON.stringify(value)) as T;
};
const taskKey = (scope: TaskDetailCacheScope, friendlyId: string): string =>
  JSON.stringify([REPRESENTATION_VERSION, scope.actorId, scope.effectiveUserId, scope.organizationId, friendlyId]);
const evictExpired = (now = Date.now()): void => {
  entries.forEach((entry, key) => { if (entry.expiresAt <= now) entries.delete(key); });
};
const enforceLimit = (): void => {
  while (entries.size > MAX_ENTRIES) {
    const oldest = Array.from(entries.entries()).sort((a, b) => a[1].lastAccessedAt - b[1].lastAccessedAt)[0];
    if (!oldest) return;
    entries.delete(oldest[0]);
  }
};

export const getCachedTaskDetail = (scope: TaskDetailCacheScope, friendlyId: string): Task | null => {
  const now = Date.now();
  evictExpired(now);
  const entry = entries.get(taskKey(scope, friendlyId));
  if (!entry) return null;
  entry.lastAccessedAt = now;
  return clone(entry.task);
};

export const fetchAndCacheTaskDetail = async (
  scope: TaskDetailCacheScope,
  friendlyId: string,
  fetcher: () => Promise<Task | null>,
): Promise<Task | null> => {
  const key = taskKey(scope, friendlyId);
  const existing = inFlight.get(key);
  if (existing) return existing.then(clone);
  const requestGeneration = generation;
  let promise: Promise<Task | null>;
  promise = fetcher()
    .then((task) => {
      if (requestGeneration !== generation) throw new TaskDetailCacheObsoleteError();
      if (task == null) { entries.delete(key); return null; }
      const now = Date.now();
      entries.set(key, { task: clone(task), expiresAt: now + TTL_MS, lastAccessedAt: now });
      enforceLimit();
      return clone(task);
    })
    .catch((error: any) => {
      if ([401, 403, 404].includes(error?.status)) entries.delete(key);
      throw error;
    })
    .finally(() => { if (inFlight.get(key) === promise) inFlight.delete(key); });
  inFlight.set(key, promise);
  return promise;
};

export const clearTaskDetailMemoryCache = (): void => {
  generation += 1;
  entries.clear();
  inFlight.clear();
};

export const invalidateTaskDetail = (scope: TaskDetailCacheScope, friendlyId: string): void => {
  generation += 1;
  const key = taskKey(scope, friendlyId);
  entries.delete(key);
  inFlight.delete(key);
};

export const shouldInvalidateTaskDetailForMutation = (method?: string, url = ""): boolean => {
  if (["GET", "HEAD", "OPTIONS"].includes((method || "GET").toUpperCase())) return false;
  const path = url.toLowerCase();
  return ["/tasks", "/taskaction", "/taskworkflow", "/workflow/", "/taskrelationship", "/compliance", "/administrative"]
    .some((segment) => path.includes(segment));
};
